import { actionCreator, ActionReturnType } from "@itk/typescript-utils";
import { TTabKey, TTabs } from "./configurator-types";
import { Model_ConfigurationsGet, Model_ConfigurationQueryFilterPost, Model_ProjectLineItemsBulkPut, Model_LabelAndReferenceGet, Model_ConfigurationBulkPut } from "src/backend/openapi/v1";

export enum Action {
  SetActiveTab = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETACTIVETAB",

  SetSelectedConfigurations = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETSELECTEDCONFIGURATIONS",
  SetIsLoading = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETISLOADING",

  GetConfigurationsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONSREQUEST",
  GetConfigurationsSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONSSUCCESS",
  GetConfigurationsFail = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONSFAIL",

  DeleteConfigurationRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:DELETECONFIGURATIONREQUEST",
  DeleteProjectLineItemsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:DELETEPROJECTLINEITEMSREQUEST",
  CopyProjectLineItemsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:COPYPROJECTLINEITEMSREQUEST",

  GetConfigurationFiltersRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONFILTERSREQUEST",
  GetConfigurationFiltersSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETCONFIGURATIONFILTERSSUCCESS",

  GetLabelsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETLABELSREQUEST",
  GetLabelsSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:GETLABELSSUCCESS",

  PostLabelsRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:POSTLABELSREQUEST",
  PostLabelsSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:POSTLABELSSUCCESS",

  SetAppliedFilter = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:SETAPPLIEDFILTER",

  Clear = "@@ITK:SRC:PAGES:CONFIGURATOR:CONFIGURATOR-STORE.TS:ACTION:CLEAR"
}

type TConfiguratorState = {
  activeTab: TTabs[TTabKey]["value"];
  isLoading: boolean;
  configurationsState?: Model_ConfigurationsGet;
  selectedConfigurations: string[];
  appliedFilter: Model_ConfigurationQueryFilterPost;
  isLabelsLoading: boolean;
  labels?: Model_LabelAndReferenceGet[];
};

const initialState: TConfiguratorState = {
  activeTab: "CONFIGURATIONS",
  isLoading: false,
  configurationsState: undefined,
  selectedConfigurations: [],
  appliedFilter: { attributes: [], labels: [] },
  isLabelsLoading: false,
  labels: undefined
};

export const configuratorActions = {
  setActiveTab: (tab: TConfiguratorState["activeTab"]) => actionCreator(Action.SetActiveTab, { tab }),

  setSelectedConfigurations: (data: TConfiguratorState["selectedConfigurations"]) => actionCreator(Action.SetSelectedConfigurations, { data }),
  setIsLoading: (data: boolean) => actionCreator(Action.SetIsLoading, { data }),

  getConfigurationsRequest: (projectId: string, filter?: Model_ConfigurationQueryFilterPost) => actionCreator(Action.GetConfigurationsRequest, { projectId, filter }),
  getConfigurationsSuccess: (data: Model_ConfigurationsGet) => actionCreator(Action.GetConfigurationsSuccess, { data }),

  getLabelsRequest: (projectId: string) => actionCreator(Action.GetLabelsRequest, { projectId }),
  getLabelsSuccess: (data: Model_LabelAndReferenceGet[]) => actionCreator(Action.GetLabelsSuccess, { data }),

  postLabelsRequest: (projectId: string, entityIds: string[], labels: string[], callback: (isSuccess: boolean, data?: Model_LabelAndReferenceGet[]) => void) =>
    actionCreator(Action.PostLabelsRequest, { projectId, entityIds, labels, callback }),

  setAppliedFilter: (filter: Model_ConfigurationQueryFilterPost) => actionCreator(Action.SetAppliedFilter, { filter }),

  deleteConfigurationsRequest: (data: Model_ConfigurationBulkPut, callBack: (isSuccess: boolean) => void) => actionCreator(Action.DeleteConfigurationRequest, { data, callBack }),
  deleteProjectLineItemsRequest: (data: Model_ProjectLineItemsBulkPut, callBack: (isSuccess: boolean) => void) => actionCreator(Action.DeleteProjectLineItemsRequest, { data, callBack }),

  copyProjectLineItemsRequest: (blankValues: boolean, data: Model_ProjectLineItemsBulkPut, callBack: (isSuccess: boolean) => void) =>
    actionCreator(Action.CopyProjectLineItemsRequest, { blankValues, data, callBack }),

  clear: () => actionCreator(Action.Clear)
};

export const configuratorReducer = (state = initialState, action: ActionReturnType<typeof configuratorActions>): TConfiguratorState => {
  switch (action.type) {
    case Action.SetActiveTab:
      return { ...state, activeTab: action.payload.tab };

    case Action.SetSelectedConfigurations:
      return { ...state, selectedConfigurations: action.payload.data };
    case Action.SetIsLoading:
      return { ...state, isLoading: action.payload.data };

    case Action.GetConfigurationsRequest:
      return action.payload.filter ? { ...state, appliedFilter: action.payload.filter } : state;
    case Action.GetConfigurationsSuccess:
      return { ...state, configurationsState: action.payload.data };

    case Action.GetLabelsRequest:
      return { ...state, isLabelsLoading: state.labels ? false : true };
    case Action.GetLabelsSuccess:
      return { ...state, isLabelsLoading: false, labels: action.payload.data };

    case Action.SetAppliedFilter:
      return { ...state, appliedFilter: action.payload.filter };

    case Action.Clear:
      return initialState;
    default:
      return state;
  }
};
